RivalIQ.module('postTags.views', function (views, App, Backbone, Marionette, $, _, moment) {

    'use strict';

    const PostTagManagerDivId = 'post-tags-manager';
    let isPostTagManagerShowing = false;
    const POSTS_TABLE_REFRESH_TIME_MS = 2000; // ms to wait after applying tags to refresh the posts table.
    const MAX_TAGS_TO_SHOW_IN_MODAL = 3;
    const MAX_POSTS_IN_MODAL = 5;
    const POSTS_TAGGED_PER_SECOND = 150; // conservatively, 200
    const SECONDS_IN_MINUTE = 60;
    const TAGGING_POSTS_MODAL_DURATION = 5000;
    const MANUAL_TAG_MODAL_DURATION = 2000;
    const AUTO_TAG_MODAL_AUTO_LOAD_WAIT_MS = 750;
    const TOOLTIP_DELAY_MS = 250;
    const MAX_DECIMAL_PLACES = 2;
    const MAX_SEARCH_TERM = 500;


    const PostTagState = {
        Selected: 'selected',
        NotSelected: 'notselected',
        Indeterminate: 'indeterminate'
    };

    // if initial state is Indeterminate, then clicking the checkbox will cycle through states in this order.
    const IndeterminateStateTransitions = [
        PostTagState.Selected,
        PostTagState.NotSelected,
        PostTagState.Indeterminate
    ];

    function isDuplicatePostTagError(error) {
        return error.errorName === 'DuplicatePostTagError';
    }

    function hasSelectedPosts(postsTable) {
        return !_.isEmpty(postsTable.getSelectedPosts()) ||
            (postsTable.getQueryModeEnabled() && postsTable.getTotalNumPosts() > 0);
    }

    function getPostTagNameTemplate(tagName) {
        return $.tagHtml('div', {
            class: 'post-tag-pill',
            html: [
                $.tagHtml('div', {
                    class: 'post-tag-pill-name',
                    html: [
                        tagName
                    ]
                })
            ].join(' ')
        });
    }

    function hasAutoTagRule(model) {
        if (!model) {
            return false;
        }
        model = (model.get ? model.attributes : model);
        return Boolean(model.post_auto_tag_id);
    }

    function formatMetricValues(metricValues) {
        return _.reduce(metricValues, function (memo, value, metric) {
            // Format percents as whole integers in the UI
            const isPercent = RivalIQ.metric.search.getPropertyValue({
                property: metric
            }, 'type') === RivalIQ.enums.dataType.ABSOLUTE_PERCENT;
            memo[metric] = isPercent ? percentToFloat(value.gte) : value.gte;
            return memo;
        }, {});
    }

    function percentToFloat(value) {
        return (value * 1000) / 10;
    }

    function floatToPercent(value) {
        return value / 100;
    }

    // This view uses the following classes for various states:
    //   - in-prompt-mode
    //   - in-saving-mode
    //   - has-error
    const EditPostTagMixin = {

        className: 'edit-section',

        // This will prevent other tags from being editable while this row is being
        // edited or deleted. Also registers a body click handler to cancel out of the mode.
        _setRowInEditMode: function () {
            const me = this;

            me.$el.parent().addClass('row-in-edit-mode');
            setTimeout(function () {
                me._handler = function (e) {
                    if ($(e.target).is(me.ui.postTagInput)) {
                        return;
                    }

                    me.exitEditMode(false);
                };

                $('body').on('click', me._handler);
            }, 0);
        },

        _cancelRowInEditMode: function () {
            const me = this;

            me.$el.parent().removeClass('row-in-edit-mode');

            if (me._handler) {
                $('body').off('click', me._handler);
                delete me._handler;
            }
        },

        _showPromptMode: function () {
            const me = this;

            me.$el.addClass('in-prompt-mode');
            me.$el.removeClass('has-error');

            me._cancelRowInEditMode();
        },

        onKeypress: function (e) {
            const me = this;

            if (e.keyCode === 13) {
                e.preventDefault();
                me.onSave();
            }
        },

        onKeyUp: function (e) {
            const me = this;

            // cancel out if the user hits escape
            if (e.keyCode === 27) {
                e.preventDefault();
                me.onCancel(e);
            }
        },

        showError: function (errorString) {
            const me = this;

            me.$el.addClass('has-error');
            me.ui.errorMessage.text(errorString);
        },

        onSave: function (e) {
            const me = this;

            _.result(e, 'stopPropagation');

            const update = {
                tag_name: me.ui.postTagInput.val()
            };

            const validationError = me.model.validate(update);
            if (validationError) {
                return me.showError(validationError);
            }

            me.model.set(update, {
                silent: true
            });
            const savePromise = me.model.save();
            if (savePromise) {
                me.$el.addClass('in-saving-mode');
                me.$el.removeClass('has-error');

                savePromise
                    .then(function () {
                        RivalIQ.postTags.data.PostTags.onPostTagCreated(me.model);
                        me.saveSuccess();
                    })
                    .done(function () {
                        me.$el.removeClass('in-saving-mode');
                    })
                    .fail(function (error) {
                        me.$el.removeClass('in-saving-mode');
                        if (isDuplicatePostTagError(error)) {
                            me.showError('Tag name already exists.');
                        } else {
                            RivalIQ.execute('alert:danger', 'An error occurred, please try again');
                        }
                    });
            }
        }
    };


    const MetricInputItem = RivalIQ.views.ItemView.mixin({
        template: '#autoTagMetricInputTemplate',
        className: 'row metric-dropdown-row',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        ui: {
            warn: '.x-warning-msg',
            metricValueInput: '.metric-range'
        },

        events: {
            'input @ui.metricValueInput': 'onMetricValueChange'
        },

        serializeData: function () {
            const me = this;

            const attrs = me.options.attributes;
            if (attrs.isPercent) {
                // convert this to whole number for the UI
                attrs.gte = percentToFloat(attrs.gte);
            }
            return attrs;
        },

        validateValue: function (newVal) {
            let value;
            let warn;
            if (!_.isNaN(Number.parseFloat(newVal))) {
                if (this.attributes.isPercent) {
                    if (_.get(newVal.toString().split('.'), 1, '').length > MAX_DECIMAL_PLACES) {
                        warn = 'Limit to two decimal places';
                    } else if (newVal < 0) {
                        warn = 'Must be greater than 0.';
                    } else {
                        value = floatToPercent(newVal);
                    }
                } else {
                    if (newVal >= 0) {
                        if (newVal > Number.MAX_SAFE_VALUE) {
                            warn = 'Choose a smaller value.';
                        }
                        value = newVal;
                    } else {
                        warn = 'Must be greater than 0.';
                    }
                }
            } else {
                warn = 'Must be numerical.';
            }

            return {
                value,
                warn
            };
        },

        onMetricValueChange: function (e) {
            const me = this;
            const val = me.$(e.target).val();
            const {value, warn} = me.validateValue(val);
            if (!_.isUndefined(value)) {
                me.hideWarningMsg();
                me.model.set('gte', value);
                me.trigger('change:value', me.model);
            } else {
                me.showWarningMsg(warn);
                me.trigger('change:value', null);
            }
        },

        hideWarningMsg: function () {
            const me = this;
            me.$el.removeClass('invalid');
        },

        showWarningMsg: function (warnMsg) {
            const me = this;
            me.ui.warn.html(warnMsg);
            me.$el.addClass('invalid');
        }
    });

    const MetricInputs = RivalIQ.views.CompositeView.mixin({
        template: '#autoTagMetricRuleInputsTemplate',
        childViewContainer: '.x-metric-inputs-container',
        childView: MetricInputItem,
        childViewOptions: (model, index) => {
            const rowTitle = index === 0 ? '' : 'AND';
            const metric = {
                property: model.get('metric')
            };
            const metricType = RivalIQ.metric.search.getPropertyValue(metric, 'type');
            model.set({
                rowTitle: rowTitle,
                title: RivalIQ.metric.names.metricName(metric),
                description: RivalIQ.metric.search.getPropertyValue(metric, 'description'),
                isPercent: metricType === RivalIQ.enums.dataType.ABSOLUTE_PERCENT
            });
            return model;
        },

        onRenderLoaded: function () {
            const me = this;
            me.$('[data-toggle="popover"]').popover();
        },

        childEvents: {
            'change:value': 'onMetricUpdate'
        },

        onMetricUpdate: function (metricUpdated) {
            const me = this;
            me.trigger('change:value', metricUpdated);
        }
    });

    const SearchBarView = RivalIQ.views.ItemView.mixin({
        template: '#postTagsSearchBarTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        ui: {
            searchInput: '#autoTagsRuleSearch',
            searchBtn: '.x-auto-tag-search',
            clearBtn: '.x-clear'
        },

        events: {
            'click @ui.clearBtn': 'clearSearch'
        },

        eventsNotToDebounce: {
            'keyup @ui.searchInput': 'onKeyUp'
        },

        getCurrentSearchTerm: function () {
            const me = this;
            return me.ui.searchInput.val().trim();
        },

        onKeyUp: function () {
            const me = this;
            me.trigger('change:searchTerm');
            me._updateCleanButtonVisibility();
        },

        serializeData: function () {
            const me = this;

            const searchTerm = me.getProp('searchTerm');
            return {
                searchTerm: searchTerm
            };
        },

        onAttachLoaded: function () {
            const me = this;
            me.ui.searchInput.focus();
        },

        onRenderLoaded: function () {
            const me = this;
            me._updateCleanButtonVisibility();
        },

        _updateCleanButtonVisibility: function () {
            const me = this;

            const hasSearchTerm = Boolean(me.getCurrentSearchTerm());
            me.ui.searchBtn.toggleClass('display-none', hasSearchTerm);
            me.ui.clearBtn.toggleClass('display-none', !hasSearchTerm);
        },

        clearSearch: function () {
            const me = this;

            me.ui.searchInput.val('');
            me.trigger('change:searchTerm', null);
            me._updateCleanButtonVisibility();
        }
    });

    const SearchResultsPreview = RivalIQ.views.LayoutView.mixin({
        template: '#autoTagRuleSearchResultsPreviewTemplate',

        className: 'search-preview-container',

        ui: {
            searchResultsSummary: '.auto-tags-rule-search-results-summary',
            socialPosts: '.x-social-posts'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        dashboardModelEvents: {
            change: 'reload'
        },

        regions: {
            socialPosts: '.x-social-posts'
        },

        showResultsSummary: function (numPosts, startDate, endDate) {
            const me = this;

            let displaySearchResultSummary = !me._getSearchProps();
            if (!_.isFinite(numPosts)) {
                displaySearchResultSummary = true;
            } else {
                const numPostsFormatted = RivalIQ.formatter.formatAbbreviated(numPosts);

                const postName = 'post';

                const text = [
                    'We found',
                    numPostsFormatted,
                    'matching',
                    RivalIQ.formatter.numericLabel(postName, `${postName}s`, numPosts),
                    `in your landscape from ${startDate.format('MMM D, YYYY')} to ${endDate.format('MMM D, YYYY')}.`
                ];

                if (numPosts > 0) {
                    text.push(`Here's a preview:`);
                }
                me.ui.searchResultsSummary.text(text.join(' '));
            }

            me.ui.searchResultsSummary.toggleClass('display-none', displaySearchResultSummary);
        },

        _getSearchProps: function () {
            const me = this;

            const postAutoTagId = me.getProp('postAutoTagId');
            if (!postAutoTagId) {
                return;
            }

            if (postAutoTagId === RivalIQ.enums.postAutoTags.UserDefinedQuery.id) {

                const searchTerm = me.getProp('searchTerm');
                if (searchTerm) {
                    return {
                        searchTerm: searchTerm
                    };
                }
            } else if (postAutoTagId === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                const metricValues = me.getProp('metricValues');
                return {
                    metricValues: metricValues
                };
            } else {
                return {
                    postAutoTagId: postAutoTagId
                };
            }
        },

        loadData: function (cb) {
            const me = this;

            me.resultsSummary = null;

            const searchProps = me._getSearchProps();
            if (_.isEmpty(searchProps)) {
                // show nothing if search term is empty.
                me.socialPosts.reset();
                return cb();
            }

            const timezone = RivalIQ.state.landscape.get('timezone');
            const startDate = moment.tz(timezone).subtract(RivalIQ.settings.historyLimits.historyLimitDays, 'd');
            const endDate = moment.tz(timezone);
            $.ajax({
                url: `/api/landscape/${RivalIQ.state.landscape.id}/searchTermPostCount`,
                type: 'GET',
                data: _.extend(searchProps, {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                }),
                success: function (result) {
                    me.resultsSummary = result;
                    me.startDate = startDate;
                    me.endDate = endDate;
                },
                complete: cb
            });
        },

        onBeforeRenderLoaded: function () {
            const me = this;
            me._showPostsWidget();
        },

        _showPostsWidget: function () {
            const me = this;

            const searchProps = me._getSearchProps();
            if (_.isEmpty(searchProps)) {
                return;
            }

            // search for posts in user's available history
            const timezone = RivalIQ.state.landscape.get('timezone');
            const mainPeriodStart = moment.tz(timezone).subtract(RivalIQ.settings.historyLimits.historyLimitDays, 'd');

            const SocialPostsWithErrorHandling = me.getSocialPostView();
            const socialPostsWidget = new SocialPostsWithErrorHandling(me.dashboardModel, {
                props: _.create(me.dashboardModel.attributes, _.extend(searchProps, {
                    landscapeId: RivalIQ.state.landscape.id,
                    widgetId: true,
                    height: 150,
                    orderBy: ['published_at'],
                    noClickThrough: true,
                    limit: MAX_POSTS_IN_MODAL,
                    timezone: RivalIQ.state.landscape.get('timezone'),
                    timePeriod: {
                        mainPeriodStart: mainPeriodStart,
                        mainPeriodEnd: moment.tz(timezone)
                    },
                    metrics: [
                        {
                            property: 'engagement_total'
                        },
                        {
                            property: 'engagement_rate'
                        }
                    ],
                    canModifyDateRange: true, // will allow timePeriod window to shrink if landscape company days exceeds max limit
                    suppressBrokenImageNotifications: true
                }))
            });

            me.socialPosts.showWidget(socialPostsWidget);
        },

        getSocialPostView: function () {
            const me = this;

            function searchUpdated(doneSuccess) {
                me.dashboardModel.set('searchSuccess', doneSuccess, {
                    silent: true
                });
                me.triggerMethod('post:search:updated');
            }

            // NOTE RivalIQ.views.SocialPostContainer does not exist when building this file so we have to
            // initialize it here
            const PostsContainerErrorHandler = RivalIQ.views.SocialPostContainer.extend({
                onBeforeRenderLoaded: function () {
                    const me = this;

                    me.listenTo(me.container, 'post:search:start', function () {
                        searchUpdated(false);
                    });
                    me.listenTo(me.container, 'post:search:fail', function (errorName) {
                        searchUpdated(false);
                        me.displayErrorView(errorName);
                    });
                    me.listenTo(me.container, 'post:search:success', function () {
                        searchUpdated(true);
                    });

                    RivalIQ.views.SocialPostContainer.prototype.onBeforeRenderLoaded.call(me);
                },

                displayErrorView: function (errorName) {
                    const me = this;
                    const errorMessage = me.getMessageForError(errorName);
                    const View = me.getSearchableErrorView();
                    const widget = new View({
                        model: new Backbone.Model({
                            message: errorMessage
                        })
                    });

                    me.container.show(widget);
                },

                getMessageForError: function (errorName) {
                    let errorMessage = '';

                    switch (errorName) {
                        case 'BadSearchSyntaxError':
                            errorMessage = 'Your search syntax is invalid. Try another search or <a href="http://help.rivaliq.com/user-guides/social-post-search-tips" target="_blank">view more search tips</a>.';
                            break;
                        case 'SearchQueryTooLongError':
                            errorMessage = `Oops! This query must be under ${MAX_SEARCH_TERM} characters.`;
                            break;
                        default:
                            errorMessage = 'Oops! An error has occurred.';
                    }

                    return errorMessage;
                },

                getSearchableErrorView: function () {
                    return RivalIQ.views.ItemView.extend({
                        template: '#invalidSearchTemplate'
                    });
                }
            });

            return RivalIQ.apps.SocialPosts.widgets.SocialPostsWidget.mixin({
                view: PostsContainerErrorHandler
            });
        },

        onRenderLoaded: function () {
            const me = this;

            me.showResultsSummary(me.resultsSummary, me.startDate, me.endDate);
        }
    });

    const AutoTagRulesGrid = RivalIQ.views.LayoutView.mixin({
        template: '#autoTagRulesGridNewTemplate',

        ui: {
            edit: '.x-edit-rule',
            delete: '.x-delete-rule',
            table: '.auto-tag-rule-table',
            tableHeader: 'th.pointer'
        },

        events: {
            'click @ui.edit': 'onEdit',
            'click @ui.delete': 'onDelete',
            'click @ui.tableHeader': 'onTableSort'
        },

        mixins: {
            postTagManagerEventing: RivalIQ.postTags.mixins.PostTagsEventingMixin
        },

        regions: {
            noRulesMsg: '.no-auto-tag-rules'
        },

        initialize: function (options) {
            const me = this;

            me.postTagsById = options.postTagsById;

            const postTagsWithRule = _.filter(me.postTagsById, function (tag) {
                return hasAutoTagRule(tag);
            });

            me.autoTagRules = RivalIQ.postTags.utils.getPostTags(_.map(postTagsWithRule, 'id'), me.postTagsById);
        },

        onRender: function () {
            const me = this;

            if (me.autoTagRules.length === 0) {
                me.$el.addClass('no-auto-tag-rules-msg');
                me.noRulesMsg.show(me.emptyView());
            }
        },

        onRenderLoaded: function () {
            const me = this;

            const template = Marionette.TemplateCache.get('#autoTagRuleGridHeaderTemplate')({});
            const header = me.$el.closest('.modal-content').find('.modal-title');
            header.html(template);

            me.createRuleBtn = header.find('.x-create-rule-btn');

            me.createRuleBtn.on('click', function () {
                RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ClickedCreateNewRule);
                me.createRuleBtn.addClass('display-none');
                me.triggerMethod('choose:post-auto-tag');
            });

            me.$('[data-toggle="tooltip"]').tooltip();
        },

        getModalTitle: function (postTag) {
            const action = (hasAutoTagRule(postTag) ? 'Edit' : 'Create');

            return $.tagHtml('div', {
                class: 'post-tag-pill-name',
                html: [
                    action,
                    ' Auto-Tag Rule for',
                    getPostTagNameTemplate(postTag.tag_name)
                ]
            });
        },

        emptyView: function () {
            const EmptyView = RivalIQ.views.ItemView.mixin({
                className: 'no-rules',
                template: '#autoTagRulesEmptyGridTemplate'
            });

            return new EmptyView();
        },

        onTableSort: function (e) {
            const me = this;

            const clickedIdx = Number($(e.currentTarget).data('col-idx'));

            const currSortColIdx = me.sortColIdx || 0;
            const currSortOrder = me.sortOrder || 'asc';

            if (clickedIdx === currSortColIdx) {
                me.sortOrder = (currSortOrder === 'desc' ? 'asc' : 'desc');
            } else {
                me.sortColIdx = (currSortColIdx === 0 ? 1 : 0);
                me.sortOrder = 'desc';
            }

            me.render();
            me.triggerMethod('rules-sorted');
        },

        serializeData: function () {
            const me = this;

            let autoTagRules = _.map(me.autoTagRules, autoTagRule => {

                if (autoTagRule.post_auto_tag_id === RivalIQ.enums.postAutoTags.UserDefinedQuery.id) {
                    return _.defaults({
                        description: _.get(autoTagRule, ['auto_tag_definition', 'searchTerm'])
                    }, autoTagRule);
                } else if (autoTagRule.post_auto_tag_id === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                    const metricValues = _.get(autoTagRule, ['auto_tag_definition', 'metricValues'], {});
                    const rawMetricValues = formatMetricValues(metricValues);
                    const rawDefinition = RivalIQ.enums.postAutoTags.findById(autoTagRule.post_auto_tag_id).detailedDescription;
                    const description = _.template(rawDefinition)(rawMetricValues);
                    return _.defaults({
                        description: description
                    }, autoTagRule);
                } else {
                    return _.defaults({
                        description: RivalIQ.enums.postAutoTags.findById(autoTagRule.post_auto_tag_id).detailedDescription
                    }, autoTagRule);
                }
            });

            const sortColIdx = me.sortColIdx || 0;
            const sortOrder = me.sortOrder || 'asc';

            autoTagRules = _.orderBy(autoTagRules,
                [postTag => postTag[(sortColIdx === 0) ? 'tag_name' : 'description'].toLocaleLowerCase()],
                [sortOrder]);

            return {
                sortColIdx: sortColIdx,
                sortOrder: sortOrder,
                autoTagRules: autoTagRules
            };
        },

        hideTooltips: function () {
            const me = this;

            me.$('[data-toggle="tooltip"]').tooltip('hide');
        },

        onEdit: function (e) {
            const me = this;

            me.hideTooltips();
            me.editRule(Number(e.target.getAttribute('post-tag-id')));
        },

        editRule: function (postTagId) {
            const me = this;

            me.createRuleBtn.addClass('display-none');
            const postTag = _.find(me.postTagsById, {
                id: postTagId
            });
            me.postTagsById[postTag.id] = postTag;
            me.$el.closest('.modal-content').find('.modal-title').html(me.getModalTitle(postTag));

            me.triggerMethod('edit:rule', postTagId);
            me.render();
        },

        onDelete: function (e) {
            const me = this;

            me.hideTooltips();
            me.createRuleBtn.addClass('display-none');
            me.triggerMethod('delete:rule', Number(e.target.getAttribute('post-tag-id')));
        }
    });

    const DeleteAutoTagView = Marionette.ItemView.mixin({
        template: '#deleteAutoTagRuleTemplate',

        ui: {
            tagToDelete: '.tag-to-delete'
        },

        serializeData: function () {
            const me = this;
            return {
                tag_name: me.tag_name
            };
        },

        initialize: function (options) {
            const me = this;

            me.tag_name = options.tag_name;
        }
    });

    const PostTagNamer = RivalIQ.views.ItemView.mixin({
        template: '#postTagNamerTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        ui: {
            postTagInput: '.x-post-tag-input',
            errorMessage: '.error-message'
        },

        eventsNotToDebounce: {
            'keyup @ui.postTagInput': 'onKeyUp'
        },

        initialize: function (options) {
            const me = this;

            me.postTagsById = options.postTagsById;
        },

        onKeyUp: function (e) {
            const me = this;

            const postTagName = me.ui.postTagInput.val();
            const errorStr = me.getErrorString(postTagName);
            if (!errorStr) {
                me.clearError();
                me.trigger('postTagNameValid', {
                    postTagName: postTagName,
                    nameSelected: e.keyCode === 13
                });
            } else {
                me.trigger('postTagNameInvalid');
                me.showError(errorStr);
            }
        },

        getErrorString: function (postTagName) {
            const me = this;
            const validationError = RivalIQ.postTags.models.PostTag.getTagNameValidationError(postTagName);
            if (validationError) {
                return validationError;
            }

            const existingPostTag = _.find(me.postTagsById, function (postTag) {
                return postTag.tag_name === postTagName;
            });
            if (existingPostTag) {
                return 'Tag name already exists.';
            }
        },

        onShow: function () {
            const me = this;

            const postAutoTagId = me.dashboardModel.get('postAutoTagId');
            const postAutoTag = RivalIQ.enums.postAutoTags.findById(postAutoTagId);
            if (postAutoTag && postAutoTag.suggestedName) {
                const allPostTags = _.values(me.postTagsById);
                let suffix = 2;
                let suggestedName = postAutoTag.suggestedName;
                while (true) {
                    const existingPostTag = _.find(allPostTags, postTag => {
                        return postTag.tag_name.toLocaleLowerCase() === suggestedName.toLocaleLowerCase();
                    });

                    if (!existingPostTag) {
                        break;
                    }

                    suggestedName = `${postAutoTag.suggestedName} ${suffix++}`;
                }

                me.ui.postTagInput.val(suggestedName);

                me.trigger('postTagNameValid', {
                    postTagName: suggestedName
                });
            } else {
                me.trigger('postTagNameInvalid');
            }

            me.ui.postTagInput.focus();
        },

        showError: function (error) {
            const me = this;

            me.ui.errorMessage.text(error);
            me.ui.errorMessage.removeClass('hidden');
        },

        clearError: function () {
            const me = this;
            me.ui.errorMessage.addClass('hidden');
        }
    });

    const PostAutoTagRuleSelector = RivalIQ.views.ItemView.mixin({
        template: '#autoTagRuleSelectorTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        ui: {
            postAutoTagRadio: 'input[type="radio"]'
        },

        events: {
            'change @ui.postAutoTagRadio': 'onPostAutoTagRadioChanged'
        },

        serializeData: function () {
            const me = this;

            const postAutoTags = _.filter(RivalIQ.enums.postAutoTags, 'id');
            const highEngagementRule = _.find(postAutoTags, {
                id: RivalIQ.enums.postAutoTags.HighEngagement.id
            });
            highEngagementRule.definition = _.template(highEngagementRule.definition)({
                engagement_rate: 'X',
                engagement_total: 'Y'
            });

            const postsAreSelected = me.getOption('postsAreSelected');
            const manualTagDescription = (postsAreSelected ?
                `This tag will be applied to the posts you've selected` :
                'Manually select posts after creating tag');

            return {
                newPostTag: me.getOption('newPostTag') || false,
                postAutoTags: postAutoTags,
                postsAreSelected: postsAreSelected,
                manualTagDescription: manualTagDescription
            };
        },

        onRender: function () {
            const me = this;

            me.$el.find('[data-toggle="popover"]').popover();
            me.$el.find('.disabled input[type="radio"]').attr('disabled', 'disabled');
        },

        onAttach: function () {
            const me = this;

            // If we have a post tag pre-selected, light up that radio button (this happens if the user clicks the back button in the modal).
            const postAutoTagId = me.dashboardModel.get('postAutoTagId');
            if (postAutoTagId) {
                me.$el.find(`input[value="${postAutoTagId}"]`).prop('checked', true);
                me.triggerMethod('auto:tag:selected', (postAutoTagId ? Number(postAutoTagId) : null));
            }
        },

        onDestroy: function () {
            $('.popover').popover('hide');
        },

        onPostAutoTagRadioChanged: function () {
            const me = this;

            const autoTagId = me.$el.find('[type=radio]:checked').val();
            // null indicates that the user selected "No Rule"
            me.triggerMethod('auto:tag:selected', (autoTagId ? Number(autoTagId) : null));
        }
    });

    const SearchTermTooLongView = Marionette.ItemView.mixin({
        template: '#searchTermTooLongTemplate',
        serializeData: function () {
            return {
                maxChars: MAX_SEARCH_TERM
            };
        }
    });

    const AutoTagRuleConfigurator = RivalIQ.views.LayoutView.mixin({
        template: '#autoTagRuleConfiguratorTemplate',

        ui: {
            searchTips: '.search-tips-link',
            searchTipsContainer: '.x-search-tips'
        },

        regions: {
            metricInputs: '.x-metric-inputs',
            searchBarContainer: '.x-auto-tag-search-bar',
            postsPreview: '.x-posts-preview',
            errorContainer: '.x-error-message'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        serializeData: function () {
            const me = this;

            const postAutoTagId = me.dashboardModel.get('postAutoTagId');

            return {
                postAutoTag: RivalIQ.enums.postAutoTags.findById(postAutoTagId)
            };
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            if (me.getProp('postAutoTagId') === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                if (!me.getProp('metricValues')) {
                    const defaultValues = RivalIQ.enums.postAutoTags.HighEngagement.defaultValues;
                    me.setProps({
                        metricValues: defaultValues
                    });
                }
                me.showMetricInputs();
            } else {
                me.showSearchBar();
            }

            me.showSearchResultsPreview();
        },

        onRenderLoaded: function () {
            const me = this;

            RivalIQ.apps.SocialPosts.utils.setupSearchTipsPopover(me.dashboardModel.id, me.ui.searchTips, {
                placement: 'right'
            });

            const postAutoTagId = me.dashboardModel.get('postAutoTagId');

            if (postAutoTagId === RivalIQ.enums.postAutoTags.UserDefinedQuery.id) {
                me.$el.find('.x-auto-tag-search-bar').show();
                me.ui.searchTipsContainer.show();
            } else if (postAutoTagId === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                me.$el.find('.x-metric-inputs').show();
            } else {
                me.ui.searchTipsContainer.hide();
                me.$el.find('.x-auto-tag-search-bar').hide();
                me.$el.find('.x-metric-inputs').hide();
            }
            me.$el.find('.x-posts-preview').show();
        },

        showSearchBar: function () {
            const me = this;

            const searchBar = new SearchBarView({
                dashboardModel: me.dashboardModel
            });

            me.searchBar = searchBar;
            me.searchBarContainer.show(searchBar);

            const debouncedSearchTermChanged = _.debounce(function () {
                me._updateSearchTerm(searchBar.getCurrentSearchTerm());
            }, AUTO_TAG_MODAL_AUTO_LOAD_WAIT_MS);

            me.listenTo(searchBar, 'change:searchTerm', function () {
                const searchTerm = searchBar.getCurrentSearchTerm();
                if (searchTerm) {
                    if (searchTerm !== me.getProp('searchTerm')) {
                        // disable the buttons until we've loaded some posts.
                        debouncedSearchTermChanged();
                    }
                } else {
                    // empty search term, immediately revert to the empty view.
                    me._updateSearchTerm('');
                }
            });
        },

        showSearchResultsPreview: function () {
            const me = this;

            me.searchResultsPreview = new SearchResultsPreview({
                dashboardModel: me.dashboardModel
            });

            me.postsPreview.show(me.searchResultsPreview, {
                forceLoadingView: true
            });

            // We only enable the "Continue" button once the preview has finished loading
            // also update on any change in search status
            me.listenTo(me.searchResultsPreview, 'render:loaded', function () {
                me.triggerMethod('update:btn:save');
            });
            me.listenTo(me.searchResultsPreview, 'post:search:updated', function () {
                me.triggerMethod('update:btn:save');
            });
        },

        _updateSearchTerm: function (searchTerm) {
            const me = this;

            if (searchTerm.length >= MAX_SEARCH_TERM) {
                me.errorContainer.show(new SearchTermTooLongView());
                me.errorContainer.$el.show();
                me.postsPreview.$el.hide();
                me.dashboardModel.set('searchSuccess', false, {
                    silent: true
                });
                me.triggerMethod('update:btn:save');
            } else if (searchTerm !== me.getProp('searchTerm')) {
                me.postsPreview.$el.show();
                me.errorContainer.$el.hide();
                me.setProps({
                    searchTerm: searchTerm
                });
                // TODO: ask doc what tracking he wants
                // RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.RulesSetSearchTerm, {
                //     searchTerm: searchTerm
                // });
            } else {
                me.triggerMethod('update:btn:save');
            }
        },

        showMetricInputs: function () {
            const me = this;

            // array-ify
            const metricValues = _.map(me.getProp('metricValues'), function (values, metric) {
                return {
                    metric: metric,
                    gte: values.gte
                };
            });
            const MetricInputsWidget = new MetricInputs({
                collection: new Backbone.Collection(metricValues)
            });

            me.metricInputsWidget = MetricInputsWidget;
            me.metricInputs.show(MetricInputsWidget);

            const debouncedMetricInputChanged = _.debounce(function (metricUpdated) {
                me._updateMetric(metricUpdated);
            }, AUTO_TAG_MODAL_AUTO_LOAD_WAIT_MS);

            me.listenTo(MetricInputsWidget, 'change:value', function (metricUpdated) {
                if (metricUpdated.$el.hasClass('invalid')) {
                    me.triggerMethod('update:btn:save');
                } else {
                    debouncedMetricInputChanged(metricUpdated);
                }
            });
        },

        _updateMetric: function (metricUpdated) {
            const me = this;
            const metricValues = _.clone(me.getProp('metricValues'));
            const metricAttrs = metricUpdated.attributes;
            metricValues[metricAttrs.metric] = {
                gte: metricAttrs.gte
            };
            me.setProps({
                metricValues: metricValues
            });
            me.triggerMethod('update:btn:save');
            me.trigger('change:value', metricValues);
            // TODO what tracking do we want?
            // RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.RulesSetSearchTerm, {
            //     searchTerm: searchTerm
            // });
        }
    });

    const POST_TAG_CREATE_STATE = {
        SELECT_RULE: 1,
        CONFIGURE_RULE: 2,
        NAME_POST_TAG: 3,
        SHOW_EXPLAINER: 4
    };

    const ApplyingTagConfirmation = RivalIQ.views.LayoutView.mixin({
        template: '#applyingAutoTagRuleTemplate',

        templateHelpers: function () {
            const me = this;

            const numMatchingPosts = me.options.matchingPosts;

            const timeEstimateMinutes = numMatchingPosts / (POSTS_TAGGED_PER_SECOND * SECONDS_IN_MINUTE);

            let timeEstimate = ' may take ';

            if (timeEstimateMinutes < 10) {
                timeEstimate += 'several minutes.';
            } else if (timeEstimateMinutes < 30) {
                timeEstimate += 'up to 30 minutes.';
            } else if (timeEstimateMinutes < 60) {
                timeEstimate += 'up to an hour.';
            } else {
                timeEstimate += 'over an hour.';
            }

            let postTagTemplate = me.options.postTagTemplate;

            if (!postTagTemplate) {
                postTagTemplate = 'these changes';
            }

            const message = `Applying ${postTagTemplate}${timeEstimate}`;

            return {
                timeMessage: message
            };
        }
    });

    const NewManualTagConfirmation = RivalIQ.views.LayoutView.mixin({
        template: '#createdManualPostTagTemplate',

        serializeData: function () {
            const me = this;
            return {
                postTagPillHtml: me.getOption('postTagTemplate')
            };
        }
    });

    const AutoTagRuleModal = views.AutoTagRuleModal = RivalIQ.views.LayoutView.mixin({
        template: '#manageAutoTagRuleTemplate',

        className: 'modal fade modal fade auto-tag-rule-modal',

        attributes: {
            tabindex: -1,
            'data-backdrop': 'static'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            postTagManagerEventing: RivalIQ.postTags.mixins.PostTagsEventingMixin
        },

        ui: {
            cancelBtn: '.x-cancel',
            saveBtn: '.x-save',
            modalContainer: '.edit-auto-tag-rule-modal',
            deleteBtn: '.x-delete',
            closeBtn: '.x-close',
            footerHelpText: '.auto-tag-help-text',
            modalContent: '.modal-content',
            modalBody: '.modal-body',
            modalTitle: '.modal-title',
            allChannelIcons: '.all-channel-icons',
            specificChannelIcons: '.specific-channel-icons'
        },

        events: {
            'click @ui.cancelBtn': 'onCancelClicked',
            'click @ui.saveBtn': 'onSaveClicked',
            'click @ui.deleteBtn': 'onDeleteClicked',
            'click @ui.closeBtn': 'onCloseClicked'
        },

        postTagManagerEvents: {
            'postTag:created': 'onPostTagCreated',
            'postTag:changed': 'onPostTagChanged',
            'postTag:deleted': 'onPostTagDeleted'
        },

        onPostTagChanged: function (postTag) {
            const me = this;

            const postTagId = postTag.get('id');
            // if postTagsById is undefined, the modal hasn't been rendered, no need to trigger event
            if (me.postTagsById) {
                me.postTagsById[postTagId] = postTag.attributes;
            }
        },

        onPostTagDeleted: function (postTag) {
            const me = this;

            if (me.postTagsById) {
                const postTagId = postTag.get('id');
                _.remove(me.postTagsById, function (tag) {
                    return tag.id === postTagId;
                });
            }
        },

        onPostTagCreated: function (postTag) {
            const me = this;

            if (me.postTagsById) {
                me.postTagsById[postTag.id] = postTag.attributes;
            }
        },

        regions: {
            autoTagView: '.edit-auto-tag-view'
        },

        serializeData: function () {
            const me = this;

            const data = {
                ruleCommand: 'Create Rule'
            };

            if (hasAutoTagRule(me.autoTagModel)) {
                data.ruleCommand = 'Update Rule';
            }
            return data;
        },

        initialize: function (options) {
            const me = this;

            me.autoTagModel = options.autoTagModel;
            me.dashboardModel = new Marionette.Dashboard.models.Model();
            me.postTagsById = options.postTagsById;
            me.showAllAutoTagRules = (Boolean(me.postTagsById) && !options.singleTagEdit && !options.newPostTag);
            me.isEditingPostAutoTag = false;

            if (options.newPostTag) {
                me.postTagCreateState = POST_TAG_CREATE_STATE.SELECT_RULE;
            } else if (me.autoTagModel) {

                me.isExistingPostTag = true;
                if (hasAutoTagRule(me.autoTagModel)) {
                    me.postTagCreateState = POST_TAG_CREATE_STATE.CONFIGURE_RULE;
                    me.isEditingPostAutoTag = true;
                } else {
                    me.postTagCreateState = POST_TAG_CREATE_STATE.SELECT_RULE;
                }
            }

            let searchTerm;

            if (me.autoTagModel) {
                searchTerm = _.get(me.autoTagModel.get('auto_tag_definition'), ['searchTerm']);
            }

            if (searchTerm) {
                me.dashboardModel.set('searchTerm', searchTerm);
            } else if (options.socialPostSearchTerm) {
                me.dashboardModel.set('searchTerm', options.socialPostSearchTerm);
            }

            me.listenTo(RivalIQ.state.landscape, 'change:postTagUpdateStatus', _.bind(me.updateInflightRules, me));
            me.listenTo(RivalIQ.state.landscape, 'change:postTagsAutoTagRulesInFlight', _.bind(me.updateInflightRules, me));
        },

        updateTitle: function () {
            const me = this;

            const prefix = (hasAutoTagRule(me.autoTagModel) ? 'Edit' : 'Create');
            const tagName = me.autoTagModel.tag_name || me.autoTagModel.get('tag_name');
            const title = $.tagHtml('div', {
                html: [
                    prefix,
                    'Auto-Tag Rule for',
                    getPostTagNameTemplate(tagName)
                ].join(' ')
            });
            me.ui.modalTitle.html(title);
        },

        onDeleteClicked: function (model) {
            const me = this;

            const tagName = model.tag_name || me.autoTagModel.tag_name || me.autoTagModel.get('tag_name');
            me.transitionDeleteMode(true, hasAutoTagRule(model), tagName);
        },

        onCloseClicked: function () {
            const me = this;

            me.hideAutoTagModal();
        },

        determineMainModalView: function () {
            const me = this;

            if (me.showAllAutoTagRules) {
                me.ui.modalContainer.removeClass('show-social-posts');
                me.ui.deleteBtn.addClass('display-none');
                me.showAllAutoTagRulesGrid();
            } else {
                me.hideAutoTagModal();
            }
        },

        onCancelClicked: function () {
            const me = this;

            if (me.ui.modalContainer.hasClass('delete-rule')) {
                return me.transitionDeleteMode(false, hasAutoTagRule(me.autoTagModel));
            }

            if (!me.isEditingPostAutoTag) {
                // if we're in the middle of the flow, need to go back one step.
                switch (me.postTagCreateState) {
                    case POST_TAG_CREATE_STATE.CONFIGURE_RULE:
                        me.showPostAutoTagRuleSelector();
                        return;

                    case POST_TAG_CREATE_STATE.NAME_POST_TAG: {
                        const postAutoTagId = me.dashboardModel.get('postAutoTagId');
                        if (!postAutoTagId) {
                            // The user had selected no rule, take them back to the rule select screen.
                            me.showPostAutoTagRuleSelector();
                        } else {
                            // The user had selected a smart rule, take them back to the rule config screen.
                            me.showAutoTagRuleConfigurator(me.autoTagModel);
                        }
                        return;
                    }

                    default:
                        // noop.
                }
            }

            // if cancel clicked in delete rule mode, return to normal modal
            me.determineMainModalView();
        },

        transitionDeleteMode: function (transitionIn, hasAutoTagRule, tagName) {
            const me = this;

            const tagTemplate = getPostTagNameTemplate(tagName);
            $('.modal-title').html(tagTemplate);

            if (transitionIn) {
                const deleteModal = new DeleteAutoTagView({
                    tag_name: tagName
                });
                me.switchView(deleteModal);
                me.ui.modalTitle.html('Are you sure you want to delete this rule?');
                me.ui.saveBtn.text('Confirm Delete');
            } else {
                const btnText = `${hasAutoTagRule ? 'Update' : 'Create'} Rule`;
                me.ui.saveBtn.text(btnText);
                me.determineMainModalView();
            }

            me.ui.saveBtn.removeClass('disabled');
            me.ui.saveBtn.toggleClass('btn-danger btn-primary');
            me.ui.modalContainer.toggleClass('delete-rule');
        },

        onSaveClicked: function () {
            const me = this;

            // delete rule
            let type;
            let data;
            let cb;
            let apiCall;

            if (me.ui.modalContainer.hasClass('delete-rule')) {
                apiCall = true;
                me.autoTagModel.set('auto_tag_definition', null);
                me.autoTagModel.set('post_auto_tag_id', null);

                type = 'DELETE';
                data = {
                    postTagId: me.autoTagModel.get('id')
                };
                cb = function () {
                    setTimeout(function () {
                        RivalIQ.postTags.data.PostTags.onPostTagChanged(me.autoTagModel);
                        RivalIQ.state.landscape.pollForNewCompanyData();
                        if (me.showAllAutoTagRules) {
                            me.transitionDeleteMode(false, hasAutoTagRule(me.autoTagModel));
                            me.showAllAutoTagRulesGrid();
                        } else {
                            me.hideAutoTagModal();
                        }
                    }, POSTS_TAGGED_PER_SECOND);
                };
            } else {

                if (me.postTagCreateState === POST_TAG_CREATE_STATE.SELECT_RULE) {

                    const postAutoTagId = me.dashboardModel.get('postAutoTagId');

                    // fire off the tracking event
                    const autoTag = _.find(RivalIQ.enums.postAutoTags, {
                        id: postAutoTagId
                    });
                    RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.SelectedAutoTagRule, {
                        postAutoTagName: _.get(autoTag, ['name'])
                    });

                    if (!postAutoTagId) {
                        // the user selected no rule.
                        me.showPostTagNamer();
                    } else {

                        // Now the user needs to configure the selected rule
                        me.autoTagModel = me.autoTagModel || new Marionette.Dashboard.models.Model({
                            tag_name: me.dashboardModel.get('postTagName'),
                            post_auto_tag_id: postAutoTagId
                        });

                        me.showAutoTagRuleConfigurator(me.autoTagModel);
                    }

                } else if (!me.isExistingPostTag && !me.showAllAutoTagRules && me.postTagCreateState === POST_TAG_CREATE_STATE.CONFIGURE_RULE) {
                    me.showPostTagNamer();
                } else {
                    const postAutoTagId = me.dashboardModel.get('postAutoTagId');

                    if (!postAutoTagId) {
                        // the user selected no rule and have named it, let's create the tag and move on.
                        return me.createNewPostTag();
                    }

                    // create / update rule
                    apiCall = true;

                    type = 'POST';

                    me.autoTagModel.set('post_auto_tag_id', postAutoTagId);
                    if (postAutoTagId === RivalIQ.enums.postAutoTags.UserDefinedQuery.id) {
                        me.autoTagModel.set('auto_tag_definition', {
                            searchTerm: me.dashboardModel.get('searchTerm')
                        });
                    } else if (postAutoTagId === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                        me.autoTagModel.set('auto_tag_definition', {
                            metricValues: me.dashboardModel.get('metricValues')
                        });
                    } else {
                        me.autoTagModel.set('auto_tag_definition', null);
                    }

                    data = {
                        postTagId: me.autoTagModel.get('id'),
                        postAutoTagId: me.autoTagModel.get('post_auto_tag_id'),
                        postTagName: me.autoTagModel.get('tag_name') || me.dashboardModel.get('postTagName')
                    };
                    data.auto_tag_definition = me.autoTagModel.get('auto_tag_definition');

                    cb = function (updatePostTagAndCount) {
                        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.CreatedPostTaggingRule, {
                            postAutoTagName: me.autoTagModel.get('tag_name')
                        });

                        me.autoTagModel = new RivalIQ.postTags.models.PostTag(updatePostTagAndCount.postTag);
                        if (!data.postTagId) {
                            RivalIQ.postTags.data.PostTags.onPostTagCreated(me.autoTagModel);
                        } else {
                            RivalIQ.postTags.data.PostTags.onPostTagChanged(me.autoTagModel);
                        }

                        RivalIQ.state.landscape.pollForNewCompanyData();

                        me.showConfirmationMsg(updatePostTagAndCount.postCount);
                    };
                }

            }

            if (apiCall) {
                me.ui.saveBtn.addClass('disabled');

                $.ajax({
                    url: `/api/landscape/${RivalIQ.state.landscape.id}/autoTagRule`,
                    type: type,
                    data: data,
                    success: cb
                })
                    .done(function () {
                        me.ui.saveBtn.removeClass('disabled');

                        setTimeout(function () {
                            me.trigger('autotag:applied');
                        }, TAGGING_POSTS_MODAL_DURATION);
                    });
            }
        },

        createNewPostTag: function () {
            const me = this;

            const newPostTag = new App.postTags.models.PostTag({
                tag_name: me.dashboardModel.get('postTagName')
            });

            const savePromise = newPostTag.save();
            if (savePromise) {
                savePromise
                    .then(function () {
                        RivalIQ.postTags.data.PostTags.onPostTagCreated(newPostTag);
                        const postTagCreatedListener = me.getOption('postTagCreatedListener');
                        if (postTagCreatedListener) {
                            postTagCreatedListener(newPostTag);
                        }

                        let showModal = false;
                        const postsAreSelected = me.getOption('postsAreSelected');
                        if (postsAreSelected) {
                            if (!RivalIQ.state.user.shouldShowTour('manualPostTagExplainer')) {
                                // if we're not showing the popover, show the modal
                                showModal = true;
                            }
                        } else {
                            showModal = true;
                        }

                        if (showModal) {
                            const confirmationMsg = new NewManualTagConfirmation({
                                postTagTemplate: getPostTagNameTemplate(me.dashboardModel.get('postTagName'))
                            });
                            me.switchView(confirmationMsg);

                            me.$el.addClass('manual-post-tag-confirmation');

                            setTimeout(function () {
                                me.hideAutoTagModal();
                            }, MANUAL_TAG_MODAL_DURATION);
                        } else {
                            me.hideAutoTagModal();
                        }
                    })
                    .fail(function (error) {
                        RivalIQ.execute('alert:danger', 'An error occurred, please try again');
                    });
            }
        },

        showConfirmationMsg: function (numMatchingPosts) {
            const me = this;

            me.ui.modalContent.addClass('show-confirmation-msg');

            // open the confirmation message
            const confirmationMsg = new ApplyingTagConfirmation({
                matchingPosts: numMatchingPosts,
                postTagTemplate: getPostTagNameTemplate(me.autoTagModel.get('tag_name'))
            });

            me.switchView(confirmationMsg);

            setTimeout(function () {
                me.ui.modalContent.removeClass('show-confirmation-msg');

                if (!me.showAllAutoTagRules) {
                    me.hideAutoTagModal();
                } else {
                    me.showAllAutoTagRulesGrid();
                }
            }, TAGGING_POSTS_MODAL_DURATION);
        },

        hideAutoTagModal: function () {
            RivalIQ.execute('hide:modal');
        },

        onDestroy: function () {
            const me = this;
            $(document).off('click', me._onCloseHandler);
        },

        onRenderLoaded: function () {
            const me = this;

            if (me.postTagCreateState === POST_TAG_CREATE_STATE.SELECT_RULE) {
                me.showPostAutoTagRuleSelector();
            } else if (me.showAllAutoTagRules) {
                me.showAllAutoTagRulesGrid();
            } else {

                const postTag = me.autoTagModel;
                const postAutoTagId = postTag.get('post_auto_tag_id');
                if (postAutoTagId) {
                    me.dashboardModel = new Marionette.Dashboard.models.Model({
                        searchTerm: _.get(postTag, ['attributes', 'auto_tag_definition', 'searchTerm']) || me.getOption('socialPostSearchTerm'),
                        metricValues: _.get(postTag, ['attributes', 'auto_tag_definition', 'metricValues'], {}),
                        postAutoTagId: postAutoTagId
                    });
                    me.showAutoTagRuleConfigurator(postTag);
                } else {
                    me.showPostAutoTagRuleSelector();
                }
            }

            if (!me._onCloseHandler) {
                me._onCloseHandler = function (e) {
                    e.stopPropagation();

                    // close on auto tag rule and rules tour views only
                    if (me.$(e.target).hasClass('modal-backdrop') &&
                     (me.$('.show-auto-tag-tour').length || me.$('.show-auto-tag-rules-grid').length)) {
                        me.hideAutoTagModal();
                        $(document).off('click', me._onCloseHandler);
                        me.destroy();
                    }
                };
                $(document).on('click', me._onCloseHandler);
            }
        },

        switchView: function (view, additionalClasses) {
            const me = this;

            const $el = me.$el.find('.edit-auto-tag-view');
            $el.removeClass().addClass('edit-auto-tag-view');
            if (additionalClasses) {
                $el.addClass(additionalClasses);
            }

            me.autoTagView.reset();
            me.autoTagView.show(view);
        },

        showPostTagNamer: function () {
            const me = this;

            me.postTagCreateState = POST_TAG_CREATE_STATE.NAME_POST_TAG;
            me.ui.modalContent.removeClass('show-auto-tag-rules-grid');

            const postTagNamer = new PostTagNamer({
                dashboardModel: me.dashboardModel,
                postTagsById: me.postTagsById
            });

            const postAutoTagId = me.dashboardModel.get('postAutoTagId');
            const stepNum = (postAutoTagId ? 3 : 2);
            me.ui.modalTitle.text(`Step ${stepNum}: Create post tag`);

            me.ui.saveBtn.text('Create Post Tag');
            me.ui.cancelBtn.text('Previous');

            me.listenTo(postTagNamer, 'postTagNameValid', function ({postTagName, nameSelected}) {
                me.ui.saveBtn.removeClass('disabled');
                me.dashboardModel.set('postTagName', postTagName, {
                    silent: true
                });

                if (nameSelected) {
                    me.onSaveClicked();
                }
            });
            me.listenTo(postTagNamer, 'postTagNameInvalid', function () {
                me.ui.saveBtn.addClass('disabled');
            });

            me.switchView(postTagNamer, 'post-tag-namer');
        },

        showAllAutoTagRulesGrid: function () {
            const me = this;

            me.isEditingPostAutoTag = false;
            me.ui.modalContent.addClass('show-auto-tag-rules-grid');
            const autoTagRulesGrid = new AutoTagRulesGrid({
                postTagsById: me.postTagsById
            });

            me.listenTo(autoTagRulesGrid, 'edit:rule', function (postTagId) {
                const postTag = _.find(me.postTagsById, {
                    id: postTagId
                });
                me.autoTagModel = new Marionette.Dashboard.models.Model(postTag);

                me.isEditingPostAutoTag = true;
                me.dashboardModel = new Marionette.Dashboard.models.Model({
                    searchTerm: _.get(postTag, ['auto_tag_definition', 'searchTerm']),
                    metricValues: _.get(postTag, ['auto_tag_definition', 'metricValues'], {}),
                    postAutoTagId: postTag.post_auto_tag_id
                });
                me.showAutoTagRuleConfigurator(postTag);
            });

            me.listenTo(autoTagRulesGrid, 'choose:post-auto-tag', function (postTagId) {
                me.autoTagModel = null;
                me.ui.modalContent.removeClass('show-auto-tag-rules-grid');
                me.dashboardModel = new Marionette.Dashboard.models.Model({});

                // TODO: move these state settings to the show methods
                // me.postTagCreateState = POST_TAG_CREATE_STATE.SELECT_RULE;
                me.showPostAutoTagRuleSelector();
            });

            me.listenTo(autoTagRulesGrid, 'delete:rule', function (postTagId) {
                me.ui.modalContent.removeClass('show-auto-tag-rules-grid');
                const model = _.find(me.postTagsById, {
                    id: postTagId
                });

                me.autoTagModel = new Marionette.Dashboard.models.Model(model);
                me.onDeleteClicked(model);
            });

            me.listenTo(autoTagRulesGrid, 'rules-sorted', function (postTagId) {
                me.updateInflightRules();
            });

            me.switchView(autoTagRulesGrid);
            me.updateInflightRules();
        },

        updateInflightRules: function () {
            const me = this;

            let postTagsAutoTagRulesInFlight = [];
            const dataFreshnessState = RivalIQ.apps.Shell.utils.getDataFreshnessState();
            if (dataFreshnessState.postTagUpdateStatus === RivalIQ.data.DataUpdateStates.UPDATE_IN_FLIGHT) {
                postTagsAutoTagRulesInFlight = _.reject(RivalIQ.state.landscape.get('postTagsAutoTagRulesInFlight'), 'complete');
            }

            me.$el.find('.auto-tag-applying-spinner').addClass('hidden');
            _.each(postTagsAutoTagRulesInFlight, function (postTag) {
                const postTagSpinnerEl = me.$el.find(`[data-post-tag-id=${postTag.id}] .auto-tag-applying-spinner`);
                postTagSpinnerEl.removeClass('hidden');
                postTagSpinnerEl.popover({
                    html: true,
                    content: 'Applying post&nbsp;tag',
                    trigger: 'hover'
                });
            });
        },

        showPostAutoTagRuleSelector: function () {
            const me = this;

            me.postTagCreateState = POST_TAG_CREATE_STATE.SELECT_RULE;
            me.dashboardModel = me.dashboardModel || new Marionette.Dashboard.models.Model({});

            me.ui.modalContent.removeClass('show-auto-tag-rules-grid');

            const postsAreSelected = me.getOption('postsAreSelected');
            const autoTagRuleSelector = new PostAutoTagRuleSelector({
                dashboardModel: me.dashboardModel,
                postTagsById: me.postTagsById,
                postsAreSelected: postsAreSelected,
                newPostTag: me.getOption('newPostTag')
            });

            me.listenTo(autoTagRuleSelector, 'auto:tag:selected', function (postAutoTagId) {
                me.dashboardModel.set('postAutoTagId', postAutoTagId);
                me.ui.saveBtn.removeClass('disabled');
            });

            let tagName = '';
            if (_.get(me.autoTagModel, ['attributes', 'tag_name'])) {
                tagName = `for ${getPostTagNameTemplate(me.autoTagModel.get('tag_name'))}`;
            }

            me.ui.modalTitle.html(`Step 1: Choose Tag Type ${tagName}`);
            me.ui.cancelBtn.text('Cancel');

            if (postsAreSelected) {
                me.ui.saveBtn.removeClass('disabled');
            } else {
                me.ui.saveBtn.addClass('disabled');
            }
            me.ui.saveBtn.text('Next');

            me.switchView(autoTagRuleSelector, 'rule-type-selector');
        },

        showAutoTagRuleConfigurator: function (postTag) {
            const me = this;

            me.postTagCreateState = POST_TAG_CREATE_STATE.CONFIGURE_RULE;
            me.ui.modalContent.removeClass('show-auto-tag-rules-grid');

            const autoTagRuleSelector = new AutoTagRuleConfigurator({
                dashboardModel: me.dashboardModel,
                postTagsById: me.postTagsById
            });

            if (me.isExistingPostTag) {
                me.ui.saveBtn.text('Add Rule');
            } else {
                me.ui.saveBtn.text('Next');
            }

            let viewClasses = 'rule-type-configurator';

            if (me.isEditingPostAutoTag) {
                const postAutoTagId = _.get(me.autoTagModel, ['attributes', 'post_auto_tag_id'], me.autoTagModel.post_auto_tag_id);
                if (RivalIQ.enums.postAutoTags.isEditable(postAutoTagId)) {
                    me.ui.saveBtn.text('Update Rule');
                } else {
                    viewClasses += ' uneditable-rule-type';
                }

                me.ui.modalContainer.addClass('show-social-posts');
                me.ui.deleteBtn.removeClass('display-none');

                me.ui.cancelBtn.text('Cancel');
            } else {
                me.ui.cancelBtn.text('Previous');
            }

            const postAutoTagConfig = RivalIQ.enums.postAutoTags.findById(me.dashboardModel.get('postAutoTagId'));
            if (postAutoTagConfig.channelIcons) {
                me.ui.allChannelIcons.hide();
                const channelIcons = _.map(postAutoTagConfig.channelIcons, channelIcon => {
                    return `<i class="fa ${channelIcon}"></i>`;
                });
                me.ui.specificChannelIcons.html(channelIcons.join(''));
                me.ui.specificChannelIcons.show();
            } else {
                me.ui.allChannelIcons.show();
                me.ui.specificChannelIcons.hide();
            }

            let modalTitle;
            const tagName = getPostTagNameTemplate(me.autoTagModel.get('tag_name'));
            switch (postAutoTagConfig.id) {
                case RivalIQ.enums.postAutoTags.UserDefinedQuery.id:
                    if (me.isEditingPostAutoTag) {
                        modalTitle = `Edit Auto-Tag Rule for ${tagName}`;
                    } else {
                        modalTitle = 'Enter Keywords or Phrases';
                    }
                    break;

                case RivalIQ.enums.postAutoTags.ContestDetection.id:
                    if (me.isEditingPostAutoTag) {
                        modalTitle = `Remove Contest or Giveaway rule from the ${tagName} tag`;
                    } else {
                        modalTitle = 'Preview Contest or Giveaway Posts';
                    }
                    break;

                case RivalIQ.enums.postAutoTags.UGCDetection.id:
                    if (me.isEditingPostAutoTag) {
                        modalTitle = `Remove User-Generated Content rule from the ${tagName} tag`;
                    } else {
                        modalTitle = 'Preview User-Generated Content Posts';
                    }
                    break;

                case RivalIQ.enums.postAutoTags.LinkInBioDetection.id:
                    if (me.isEditingPostAutoTag) {
                        modalTitle = `Remove Link in Bio rule from the ${tagName} tag`;
                    } else {
                        modalTitle = 'Preview Link in Bio Posts';
                    }
                    break;

                case RivalIQ.enums.postAutoTags.HighEngagement.id:
                    if (me.isEditingPostAutoTag) {
                        modalTitle = `Edit the engagement rate and total for the ${tagName} tag`;
                    } else {
                        modalTitle = 'Enter Engagement Rate and Total';
                    }
                    break;

                default:
                    modalTitle = 'Preview Auto-Tag Rule';
            }

            if (!me.isEditingPostAutoTag) {
                modalTitle = `Step 2: ${modalTitle}`;
                if (me.autoTagModel.get('tag_name')) {
                    modalTitle += ` for ${tagName}`;
                }
            }
            me.ui.modalTitle.html(modalTitle);

            me.switchView(autoTagRuleSelector, viewClasses);

            if (postTag && postTag.postAutoTagId) {
                me.ui.saveBtn.toggleClass('disabled', false);
            } else {
                me.ui.saveBtn.toggleClass('disabled', true);
            }

            me.listenTo(autoTagRuleSelector, 'update:btn:save', function () {
                const postAutoTagId = me.dashboardModel.get('postAutoTagId');
                let isStepComplete;
                if (postAutoTagId === RivalIQ.enums.postAutoTags.HighEngagement.id) {
                    isStepComplete = !me.$('.invalid').length;
                } else {
                    // NOTE for user defined queries, make sure there is a search term, and that the search has completed successfully
                    isStepComplete = postAutoTagId &&
                        (postAutoTagId !== RivalIQ.enums.postAutoTags.UserDefinedQuery.id ||
                            (!_.isEmpty(_.trim(me.dashboardModel.get('searchTerm'))) && me.dashboardModel.get('searchSuccess')));
                }
                me.ui.saveBtn.toggleClass('disabled', !isStepComplete);
            });
        }
    });

    const PostTagListItemView = Marionette.ItemView.mixin({
        template: '#postTagsListViewItemTemplate',
        className: function () {
            const me = this;
            return `post-tag-row edit-section post-tag-${me.model.get('id')}`;
        },

        mixins: {
            editPostTag: EditPostTagMixin
        },

        ui: {
            postTagName: '.post-tag-name',
            editBtn: '.edit-btn',
            deleteBtn: '.delete-btn',
            errorMessage: '.error-message',
            controlsContainer: '.controls',
            doneBtn: '.x-done',
            cancelBtn: '.x-cancel',
            postTagInput: '.x-post-tag-input',
            savingSpinner: '.fa-spin',
            selectedCheckbox: 'input[type="checkbox"]',
            ruleControls: '.rule-controls',
            tooltips: '[data-toggle="tooltip"]',
            rulesBtn: '.edit-rule-btn'
        },

        events: {
            'click @ui.postTagName': 'onPostTagClicked',
            'click @ui.editBtn': 'onEdit',
            'click @ui.deleteBtn': 'onDelete',
            'click @ui.doneBtn': 'onSave',
            'click @ui.cancelBtn': 'onCancel',
            'keypress input': 'onKeypress',
            'keyup input': 'onKeyUp'
        },

        eventsNotToDebounce: {
            'change @ui.selectedCheckbox': 'onCheckboxChanged'
        },

        triggers: {
            'click .edit-rule-btn': 'show:edit'
        },

        modelEvents: {
            'change': 'render'
        },

        serializeData: function () {
            const me = this;
            const data = me.model.toJSON();
            data.postTagHtml = App.postTags.utils.postTagHtml(data.tag_name);

            const activeState = me.model.get('activeState');
            data.disabled = !hasSelectedPosts(me.getOption('postsTable')) &&
                (!activeState || activeState !== PostTagState.Selected); // This latter condition is for when we're forcing the check to be selected after a user just created a new one.
            return data;
        },

        updateCheckboxUi: function (checkboxState) {
            const me = this;

            // indeterminate state cannot be set in html, so do it here after rendering.
            switch (checkboxState) {
                case PostTagState.Selected:
                    me.ui.selectedCheckbox.prop('checked', true);
                    break;
                case PostTagState.NotSelected:
                    me.ui.selectedCheckbox.prop('checked', false);
                    break;
                case PostTagState.Indeterminate:
                    me.ui.selectedCheckbox.prop('indeterminate', true);
                    break;
                default:
                    break;
            }


            const activeState = me.model.get('activeState');
            const disabled = !hasSelectedPosts(me.getOption('postsTable')) &&
                (!activeState || activeState !== PostTagState.Selected); // This latter condition is for when we're forcing the check to be selected after a user just created a new one.
            me.ui.selectedCheckbox.prop('disabled', disabled);
        },

        onRender: function () {
            const me = this;

            // check if the user has intentionally set this bit.
            let checkboxState = me.model.get('activeState');
            if (!checkboxState) {
                // they haven't -- show the checkbox state as reflecting any selected posts.
                checkboxState = me.model.get('initialState');
            }

            me.updateCheckboxUi(checkboxState);

            if (hasAutoTagRule(me.model)) {
                me.ui.rulesBtn.attr('title', 'Edit Auto-Tag Rule');
                me.ui.ruleControls.addClass('show-rule');
            } else {
                me.ui.rulesBtn.attr('title', 'Create Auto-Tag Rule');
                me.ui.ruleControls.removeClass('show-rule');
            }

            me.ui.tooltips.tooltip({
                delay: {
                    show: TOOLTIP_DELAY_MS,
                    hide: 0
                }
            });

            me._showPromptMode();
        },

        onDelete: function () {
            const me = this;

            $.ajax({
                url: `/api/landscape/${RivalIQ.state.landscape.id}/postTag/${me.model.get('id')}/taggedPostCount`,
                type: 'GET',
                success: function (result) {
                    me.postsWithTag = result.postCount;
                    me.hitLimit = result.hitLimit;
                },
                complete: function () {
                    me.showDeleteModal();
                }
            });
        },

        showDeleteModal: function () {
            const me = this;

            const numPostsAffected = me.postsWithTag || 0;
            let modalText = '';
            const tagHtml = getPostTagNameTemplate(me.model.get('tag_name'));
            if (numPostsAffected > 0) {
                modalText = [
                    'Deleting this tag will remove',
                    tagHtml,
                    'from ',
                    (me.hitLimit ? 'more than ' : ''),
                    numPostsAffected,
                    ' ',
                    RivalIQ.formatter.numericLabel('post', 'posts', numPostsAffected),
                    '. This cannot be undone.'
                ].join('');
            } else {
                modalText = 'Deleting this tag cannot be undone.';
            }

            const modalInfo = {
                className: 'post-tag-pills',
                information: {
                    title: `Are you sure you want to delete${tagHtml}?`,
                    showTitleAsHtml: true
                },
                contentHtml: modalText,
                actions: [
                    {
                        html: 'Cancel',
                        className: 'btn btn-default',
                        attributes: {
                            type: 'button',
                            'data-dismiss': 'modal'
                        }
                    },
                    {
                        html: 'Confirm Delete',
                        className: 'btn btn-danger',
                        events: {
                            click: function () {
                                RivalIQ.postTags.data.PostTags.onPostTagDeleted(me.model);
                                me.model.destroy();
                            }
                        },
                        attributes: {
                            type: 'button',
                            'data-dismiss': 'modal'
                        }
                    }
                ]
            };
            RivalIQ.execute('show:modal', modalInfo);
        },

        onPostTagClicked: function () {
            const me = this;
            me.ui.selectedCheckbox.click();
        },

        onEdit: function () {
            const me = this;

            const postTagName = me.model.get('tag_name');
            me.inEditMode = true;
            me.previousValue = postTagName;
            me.$el.removeClass('in-prompt-mode');
            me.$el.addClass('in-edit-mode');

            me._setRowInEditMode();

            me.ui.postTagInput.val(postTagName);
            me.ui.postTagInput.focus();
            me.ui.postTagInput.select();
        },

        saveSuccess: function () {
            const me = this;

            me.exitEditMode(true);
        },

        onShowEdit: function () {
            const me = this;

            me.ui.tooltips.tooltip('hide');
        },

        exitEditMode: function (saveSuccess) {
            const me = this;

            if (!saveSuccess && me.previousValue) {
                // need to reset to the previous value if the user kept setting the name to the duplicate
                // value, then gave up.
                me.model.set('tag_name', me.previousValue);
            }
            delete me.previousValue;

            me.inEditMode = false;
            me.$el.removeClass('in-edit-mode has-error');
            me.$el.addClass('in-prompt-mode');

            me._cancelRowInEditMode();
        },

        onCancel: function (e) {
            const me = this;

            e.stopPropagation();
            me.exitEditMode(false);
        },

        onCheckboxChanged: function () {
            const me = this;

            if (me.model.get('initialState') === PostTagState.Indeterminate) {
                const currentState = me.model.get('activeState') || PostTagState.Indeterminate;
                const nextStateIdx = (_.indexOf(IndeterminateStateTransitions, currentState) + 1) % IndeterminateStateTransitions.length;
                const newState = IndeterminateStateTransitions[nextStateIdx];
                me.updateCheckboxUi(newState);

                if (newState === PostTagState.Indeterminate) {
                    me.model.unset('activeState');
                } else {
                    me.model.set('activeState', newState);
                }
            } else {
                const isSelected = me.ui.selectedCheckbox.prop('checked');
                const newActiveState = (isSelected ? PostTagState.Selected : PostTagState.NotSelected);
                me.model.set('activeState', newActiveState);
                me.updateCheckboxUi(newActiveState);
            }
        }
    });

    const PostTagsListView = RivalIQ.views.CompositeView.mixin({
        template: '#postTagsListViewTemplate',
        childViewContainer: '.x-post-tags-list',
        childView: PostTagListItemView,

        mixins: {
            postTagManagerEventing: RivalIQ.postTags.mixins.PostTagsEventingMixin
        },

        postTagManagerEvents: {
            'postTag:created': 'onPostTagCreated',
            'postTag:changed': 'onPostTagChanged',
            'postTag:deleted': 'onPostTagDeleted'
        },

        onPostTagChanged: function (postTag) {
            const me = this;

            const postTagId = postTag.get('id');
            const model = _.find(me.collection.models, function (model) {
                return model.get('id') === postTagId;
            });

            if (model) {
                _.each(postTag.changed, function (value, key) {
                    model.set(key, value);
                });

                if (_.isEmpty(postTag.changed)) {
                    model.set(postTag.attributes);
                }
            }
        },

        onPostTagCreated: function (postTag) {
            const me = this;

            me.collection.add(postTag);
        },

        onPostTagDeleted: function (postTag) {
            const me = this;

            me.collection.remove(postTag);
        },

        onChildviewShowEdit: function (options) {
            const me = this;

            const autoTagModel = options.model;
            let trackingEvent;

            if (hasAutoTagRule(autoTagModel)) {
                trackingEvent = RivalIQ.tracking.metric.ClickedEditRule;
            } else {
                trackingEvent = RivalIQ.tracking.metric.ClickedCreateRule;
            }

            const props = me.getOption('postsTable').getProps();
            const postTagsById = _(me.collection.models)
                .map('attributes')
                .keyBy('id')
                .value();
            const modal = new AutoTagRuleModal({
                postTagsById: postTagsById,
                singleTagEdit: true,
                autoTagModel: autoTagModel,
                socialPostSearchTerm: props.searchTerm
            });

            RivalIQ.execute('show:modal', modal);

            me.listenTo(modal, 'autotag:applied', function () {
                // refresh social post cache and reload
                RivalIQ.apps.SocialPosts.data.invalidateAllSocialPostsCaches();
                RivalIQ.apps.Insights.data.post.invalidateCaches();
                RivalIQ.postTags.data.PostTags.invalidateCaches();
                me.getOption('postsTable').resetSelectedPosts();

                me.getOption('postsTable').getCuration().reload();
            });

            autoTagModel.on('change', function () {
                me.reload();
            });

            RivalIQ.execute('tracking:push:auth', trackingEvent);
        },

        emptyView: RivalIQ.views.ItemView.extend({
            className: 'no-post-tags',
            template: '#postTagsListEmptyViewTemplate'
        }),

        collectionEvents: {
            'change': 'onPostTagsChange'
        },

        childViewOptions: function () {
            const me = this;
            return {
                postsTable: me.getOption('postsTable')
            };
        },

        onPostTagsChange: function () {
            const me = this;

            me.collection.sort();
        }
    });

    const ConfirmExitModal = RivalIQ.views.ItemView.mixin({
        className: 'post-tag-manager-exit-confirm',
        template: '#postTagsManagerConfirmExitModalTemplate',

        ui: {
            confirmExitBtn: '.x-confirm-exit',
            cancelBtn: '.x-cancel'
        },

        events: {
            'click @ui.confirmExitBtn': 'onConfirmExit',
            'click @ui.cancelBtn': 'onCancelExit'
        },

        onConfirmExit: function () {
            const me = this;

            me.getOption('postTagManager').onManagedClosed(true);
        },

        onCancelExit: function () {
            RivalIQ.execute('hide:modal');
        }
    });

    let postTagManager;

    views.PostTagManager = RivalIQ.views.LayoutView.mixin({
        template: '#postTagsManagerTemplate',

        // This is done as a function to avoid dependency loading races.
        behaviors: function () {
            return {
                Resizes: {
                    handler: 'updateListScrollBarVisibility',
                    behaviorClass: RivalIQ.graph.behaviors.Resizes
                }
            };
        },

        ui: {
            postTagApplyControls: '.post-tags-apply-controls',
            applyBtn: '.x-apply',
            cancelBtn: '.x-cancel',
            manageCurrentRules: '.manage-current-rules',
            newPostTagBtn: '.x-new-post-tag'
        },

        regions: function () {
            return {
                postTagsListRegion: '.post-tags-list-container'
            };
        },

        events: {
            'click @ui.applyBtn': 'onApply',
            'click @ui.cancelBtn': 'onCancel',
            'click @ui.manageCurrentRules': 'onManageCurrentRules',
            'click @ui.newPostTagBtn': 'onNewPostTagClicked'
        },

        initialize: function () {
            const me = this;

            me.collection = new App.postTags.collections.PostTags();

            me.setPostsTable(me.getOption('postsTable'));

            me._onCloseListener = _.bind(me.closeManager, me);
            $('.x-close-manager').on('click', me._onCloseListener);

            // This is to handle the user clicking the browser back button instead of closing the tag manager
            // via the `X`.
            me.backListener = $(window).on('popstate', function () {
                const isInPostTagEditMode = _.includes(window.location.search, 'isInPostTagEditMode=true');

                if (!isInPostTagEditMode) {
                    me.onManagedClosed(false);
                }

                return false;
            });
        },

        closeManager: function () {
            const me = this;

            if (hasSelectedPosts(me.postsTable)) {
                const modal = new ConfirmExitModal({
                    postTagManager: me
                });

                RivalIQ.execute('show:modal', {
                    size: 'small',
                    contentView: modal
                });
            } else {
                me.onManagedClosed(true);
            }
        },

        onManageCurrentRules: function () {
            const me = this;

            let curation;

            if (me.dashboardModel) {
                curation = me.dashboardModel.getCuration();
            } else {
                curation = me.postsTable.getCuration();
            }
            const postTagsById = _.keyBy(curation.postTags, 'id');
            const modal = new RivalIQ.postTags.views.AutoTagRuleModal({
                socialPostSearchTerm: curation.getProp('searchTerm'),
                postTagsById: postTagsById
            });

            RivalIQ.execute('show:modal', modal);
            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.OpenedAutoTagRuleModal);
        },

        onNewPostTagClicked: function () {
            const me = this;

            const selectedPosts = me.postsTable.getSelectedPosts();

            let curation;
            if (me.dashboardModel) {
                curation = me.dashboardModel.getCuration();
            } else {
                curation = me.postsTable.getCuration();
            }
            const postTagsById = _.keyBy(curation.postTags, 'id');
            const postsAreSelected = !_.isEmpty(selectedPosts);

            const postTagCreatedListener = (newPostTag) => {

                if (postsAreSelected) {
                    const newPostTagId = newPostTag.get('id');
                    const postTagInList = me.collection.find({
                        id: newPostTagId
                    });

                    me.forceSelectionEnabledTags = (me.forceSelectionEnabledTags || []).concat(newPostTagId);

                    // This will select the newly created post tag in the post tag list.
                    postTagInList.set('activeState', PostTagState.Selected);

                    if (RivalIQ.state.user.shouldShowTour('manualPostTagExplainer')) {
                        const applyBtn = $('.post-tags-apply-controls .x-apply');
                        applyBtn.popover({
                            content: Marionette.TemplateCache.get('#newTagExplainerTourTemplate'),
                            trigger: 'manual',
                            placement: 'right',
                            container: 'body',
                            html: true
                        });
                        $('body').addClass('post-tags-tour-1');
                        applyBtn.on('shown.bs.popover', function () {
                            $('.post-tags-explainer-popover .btn').click(() => {
                                if ($('.post-tags-explainer-popover input').prop('checked')) {
                                    RivalIQ.state.user.completeTour('manualPostTagExplainer');
                                }

                                $('body').removeClass('post-tags-tour-1');
                                applyBtn.popover('hide');
                            });
                        });
                        applyBtn.popover('show');
                    }
                }
            };

            const modal = new RivalIQ.postTags.views.AutoTagRuleModal({
                socialPostSearchTerm: curation.getProp('searchTerm'),
                postsAreSelected: postsAreSelected,
                postTagsById: postTagsById,
                newPostTag: true,
                postTagCreatedListener: postTagCreatedListener
            });

            RivalIQ.execute('show:modal', modal);
        },

        onManagedClosed: function (navigate) {
            const me = this;

            me.postsTable.resetSelectedPosts();
            isPostTagManagerShowing = false;

            me.backListener.off();

            App.postTags.utils.updateBodyClass(false);
            me.collection.off(null, null, me);
            me.postsTable.off(null, null, me);
            $('.x-close-manager').off('click', me._onCloseListener);

            if (navigate) {
                const propsToRemove = _.keys(RivalIQ.enums.searchFieldNames).concat('type', 'tweetType', 'promotedPosts', 'postTagIds', 'isInPostTagEditMode', 'instanceId');

                const props = _.pick(me.postsTable.getProps(), RivalIQ.apps.SocialPosts.utils.validCurationOptions);

                const curationId = me.postsTable.getProp('fromCurationId') || RivalIQ.enums.curation.POSTTAGS;
                const pathProps = _.omit(props, propsToRemove);
                const fromCurationCompanyId = me.postsTable.getProp('fromCurationCompanyId');
                if (fromCurationCompanyId) {
                    pathProps.companyId = fromCurationCompanyId;
                }

                const path = me.postsTable.curation.router.resolveCurationRoute(curationId, pathProps);
                Backbone.history.navigate(path, {
                    trigger: true
                });
            }

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ClosedPostTagManager, {});

            me.destroy();
            postTagManager = null;
        },

        onLoadedPostsChanged: function () {
            const me = this;

            const posts = me.postsTable.getLoadedPosts();
            me.postNativeIdToPostTags = _.reduce(posts, function (memo, post) {
                const postKey = App.postTags.utils.makePostKey(RivalIQ.enums.sourceDetails[post.get('source')].channel, post.get('native_id'));
                memo[postKey] = post.get('post_tag_ids');

                return memo;
            }, {});

            me.updateInitialSelectionState();
        },

        loadData: function () {
            const me = this;

            return me.collection.fetch()
                .then(function () {
                    me.updateInitialSelectionState();
                });
        },

        updateInitialSelectionState: function () {
            const me = this;

            const selectedPosts = me.postsTable.getSelectedPosts();
            const numSelectedPosts = (me.postsTable.getQueryModeEnabled() ? me.postsTable.getTotalNumPosts() : selectedPosts.length);
            me.collection.each(function (postTag) {

                let numSelectedPostsWithPostTag;
                if (me.postsTable.getQueryModeEnabled()) {
                    numSelectedPostsWithPostTag = _.get(me.postsTable.getPostTagCounts(), [postTag.id]) || 0;
                } else {
                    numSelectedPostsWithPostTag = _.sumBy(selectedPosts, function (selectedPost) {
                        const postKey = selectedPost.postKey;
                        return _.includes(me.postNativeIdToPostTags[postKey], postTag.id) ? 1 : 0;
                    });
                }

                let initialState;
                if (numSelectedPostsWithPostTag === 0) {
                    initialState = PostTagState.NotSelected;
                } else if (numSelectedPostsWithPostTag === numSelectedPosts) {
                    initialState = PostTagState.Selected;
                } else {
                    initialState = PostTagState.Indeterminate;
                }

                postTag.set({
                    initialState: initialState
                });
            });
        },

        updateControlsVisibility: function () {
            const me = this;

            if (!me.isRendered) {
                return;
            }

            const selectedPosts = hasSelectedPosts(me.postsTable);

            me.postTagsListRegion.$el.toggleClass('no-posts-selected', !selectedPosts && _.isEmpty(me.forceSelectionEnabledTags));
            me.$el.find('input[type="checkbox"]').prop('disabled', !selectedPosts && _.isEmpty(me.forceSelectionEnabledTags));

            if (_.isString(me.ui.postTagApplyControls)) {
                return;
            }

            const hasActiveSelections = Boolean(me.collection.find(function (postTag) {
                const activeState = postTag.get('activeState');
                return activeState && activeState !== postTag.get('initialState');
            }));

            const controlsHidden = !selectedPosts || !hasActiveSelections;
            me.ui.postTagApplyControls.toggleClass('hidden', controlsHidden);

            me.updateListScrollBarVisibility();
        },

        onRenderLoaded: function () {
            const me = this;

            me.postTagsListRegion.show(new PostTagsListView({
                collection: me.collection,
                postsTable: me.postsTable
            }));

            me.updateListScrollBarVisibility();
            me.bindCollectionEvents();
        },

        updateListScrollBarVisibility: function () {
            const me = this;

            const listAvailableHeight = $('.post-tags-list-section').get(0).clientHeight - $('.post-tags-apply-controls').get(0).clientHeight;
            const $listEl = me.$el.find('.post-tags-list-container');
            const needsScrollbars = $listEl.get(0).scrollHeight > listAvailableHeight;

            $listEl.toggleClass('show-scrollbars', needsScrollbars);
        },

        bindCollectionEvents: function () {
            const me = this;

            me.collection.on('add', function () {
                me.updateControlsVisibility();
            });

            me.collection.on('change', function (postTag) {
                RivalIQ.postTags.data.PostTags.onPostTagChanged(postTag);
            });

            me.collection.on('remove', function (postTag) {
                RivalIQ.postTags.data.PostTags.onPostTagDeleted(postTag);
                if (me.forceSelectionEnabledTags) {
                    me.forceSelectionEnabledTags = _.remove(me.forceSelectionEnabledTags, postTag.id);
                }
                me.updateControlsVisibility();
            });

            me.collection.on('change:activeState', function () {
                me.updateControlsVisibility();
            });
        },

        setPostsTable: function (postsTable) {
            const me = this;

            if (me.postsTable === postsTable) {
                return;
            }

            let reloadViews = false;
            if (me.postsTable) {
                me.postsTable.off(null, null, me);
                reloadViews = true;
            }

            me.postsTable = postsTable;
            me.postsTable.on('selectedPostsChanged', me.onSelectedPostsChanged, me);
            me.postsTable.on('selectedPostsReset', me.onSelectedPostsChanged, me);
            me.postsTable.on('loadedPostsChanged', me.onLoadedPostsChanged, me);
            me.postsTable.on('queryModeEnabledChanged', me.onSelectedPostsChanged, me);

            if (reloadViews) {
                me.reload();
            }
            me.onLoadedPostsChanged();
        },

        onSelectedPostsChanged: function () {
            const me = this;

            // Order matters here; we need to update the initial selection state first as
            // that is diffed against the active state when we're deciding if the buttons
            // are visible.
            me.updateInitialSelectionState();
            me.updateControlsVisibility();

            // when no posts are selected, reset all the active states.
            // NOTE: this means that if you select a tag, then a post, then unselect the post, the tag will be unselected.
            if (!hasSelectedPosts(me.postsTable)) {
                me.collection.each(function (postTag) {
                    if (!_.includes(me.forceSelectionEnabledTags, postTag.get('id'))) {
                        postTag.unset('activeState');
                    }
                });
            }

        },

        _onTagsAppliedOnServer: function () {
            const me = this;

            const deferred = new $.Deferred();
            setTimeout(function () {
                RivalIQ.apps.SocialPosts.data.invalidateAllSocialPostsCaches();
                RivalIQ.apps.Insights.data.post.invalidateCaches();
                RivalIQ.postTags.data.PostTags.invalidateCaches();
                me.postsTable.resetSelectedPosts();

                me.dashboardModel.getCuration().reload();
                deferred.resolve();
            }, POSTS_TABLE_REFRESH_TIME_MS);

            return deferred;
        },

        onApply: function () {
            const me = this;

            const postTags = me.collection.reduce(function (memo, postTag) {

                const activeState = postTag.get('activeState');
                if (!_.isUndefined(activeState)) {
                    if (activeState === PostTagState.Selected) {
                        memo.postTagsToAdd.push(postTag.get('id'));
                    } else {
                        memo.postTagsToRemove.push(postTag.get('id'));
                    }
                }

                return memo;
            }, {
                postTagsToAdd: [],
                postTagsToRemove: []
            });

            const queryModeEnabled = me.postsTable.getQueryModeEnabled();
            if (queryModeEnabled) {
                me._applyTagsToAllPosts(postTags.postTagsToAdd, postTags.postTagsToRemove);
            } else {
                me._applyTagsToSpecificPosts(postTags.postTagsToAdd, postTags.postTagsToRemove);
            }

            delete me.forceSelectionEnabledTags;

            const numPosts = (queryModeEnabled ? me.postsTable.getTotalNumPosts() : _.size(me.postsTable.getSelectedPosts()));
            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.AppliedPostTags, {
                queryModeEnabled: queryModeEnabled,
                numPosts: numPosts,
                numTagsToAdd: _.size(postTags.postTagsToAdd),
                numTagsToRmove: _.size(postTags.postTagsToRemove),
                source: 'postTagsManager'
            });
        },

        _applyTagsToAllPosts: function (postTagsToAdd, postTagsToRemove) {
            const me = this;

            const numPosts = me.postsTable.getTotalNumPosts();

            if (numPosts === 0) {
                return;
            }

            const modal = new ApplyingTagConfirmation({
                matchingPosts: numPosts
            });

            RivalIQ.execute('show:modal', {
                size: 'large',
                className: 'auto-tag-rule-modal',
                contentView: modal
            });

            const postsQuery = me.postsTable.getPostsQuery();
            const url = `/api/landscape/${RivalIQ.state.landscape.id}/postTagAssociationJobs`;
            $.ajax({
                type: 'PUT',
                url: url,
                data: {
                    post_tags_to_add: postTagsToAdd,
                    post_tags_to_remove: postTagsToRemove,
                    postsQuery: postsQuery
                },
                success: function (job) {

                    RivalIQ.state.landscape.pollForNewCompanyData();
                    setTimeout(function () {

                        $.get(`/api/postTagAssociationJobs/${job.id}`)
                            .then(function (jobStatus) {
                                if (_.get(jobStatus, ['is_complete'])) {
                                    me._onTagsAppliedOnServer()
                                        .then(function () {
                                            me.hideSavingModal();
                                        });
                                } else {
                                    me.postsTable.resetSelectedPosts();
                                    me.hideSavingModal();
                                }
                            })
                            .fail(function () {
                                RivalIQ.state.landscape.pollForNewCompanyData();
                                me.postsTable.resetSelectedPosts();
                                me.hideSavingModal();
                            });

                    }, TAGGING_POSTS_MODAL_DURATION);
                },
                error: function () {
                    me.hideSavingModal();
                    RivalIQ.execute('alert:danger', 'An error occurred, please try again');
                }
            });
        },

        _applyTagsToSpecificPosts: function (postTagsToAdd, postTagsToRemove) {
            const me = this;

            const posts = _(me.postsTable.getSelectedPosts())
                .groupBy('channel')
                .map(function (posts, channel) {

                    return {
                        channel: channel,
                        postNativeIds: _.map(posts, 'postNativeId')
                    };
                })
                .value();

            if (_.isEmpty(posts)) {
                return;
            }

            const numPosts = _(posts)
                .map('postNativeIds')
                .flatten()
                .value()
                .length;
            me.showSavingModal(numPosts, postTagsToAdd, postTagsToRemove);

            RivalIQ.postTags.data.PostTags.updateAssociations(postTagsToAdd, postTagsToRemove, posts)
                .done(function () {
                    me._onTagsAppliedOnServer()
                        .then(function () {
                            me.hideSavingModal();
                        });
                })
                .fail(function () {
                    me.hideSavingModal();
                    RivalIQ.execute('alert:danger', 'An error occurred, please try again');
                });
        },

        showSavingModal: function (numPosts, postTagsToAdd, postTagsToRemove) {
            const me = this;

            const postTagsById = _.mapValues(me.collection.indexBy('id'), 'attributes');

            function getPostTagsString(postTagIds) {
                let postTags = _(RivalIQ.postTags.utils.getPostTags(postTagIds, postTagsById))
                    .map('postTagHtml')
                    .map(function (postTagHtml) {
                        return getPostTagNameTemplate(postTagHtml);
                    })
                    .value();

                if (postTags.length > MAX_TAGS_TO_SHOW_IN_MODAL) {
                    postTags = _.take(postTags, MAX_TAGS_TO_SHOW_IN_MODAL - 1);

                    const othersCount = postTagIds.length - postTags.length;
                    postTags.push(['and', othersCount, RivalIQ.formatter.numericLabel('other', 'others', othersCount)].join(' '));
                }

                return postTags.join(', ');
            }

            let postTagSavingMessage = '';

            if (!_.isEmpty(postTagsToAdd)) {
                postTagSavingMessage +=
                $.tagHtml('div', {
                    html: [
                        'Applying',
                        getPostTagsString(postTagsToAdd),
                        'to',
                        numPosts,
                        RivalIQ.formatter.numericLabel('post.', 'posts.', numPosts)
                    ].join(' ')
                });
            }

            if (!_.isEmpty(postTagsToRemove)) {
                if (!_.isEmpty(postTagSavingMessage)) {
                    postTagSavingMessage += '&nbsp;';
                }
                postTagSavingMessage +=
                $.tagHtml('div', {
                    html: [
                        'Removing',
                        getPostTagsString(postTagsToRemove),
                        'from',
                        RivalIQ.formatter.numericLabel('post.', 'posts.', numPosts)
                    ].join(' ')
                });
            }

            RivalIQ.execute('show:modal', Marionette.TemplateCache.get('#postTagsSavingModal')({
                postTagSavingMessage: postTagSavingMessage
            }));
        },

        hideSavingModal: function () {
            RivalIQ.execute('hide:modal');
        },

        onCancel: function () {
            const me = this;

            // Order matters here; resetSelectedPosts will trip re-rending the checkboxes, which looks at forceSelectionEnabledTags.
            delete me.forceSelectionEnabledTags;
            me.postsTable.resetSelectedPosts();
        }
    });

    function showPostTagManager(postsTable) {
        let container = $(`#${PostTagManagerDivId}`);
        if (!container.length) {
            container = $('<div/>', {
                id: PostTagManagerDivId
            });
            container.appendTo('body');
        }

        // inject post tags edit message to top of the page
        let editMessageContainer = $('#post-tags-edit-header')[0];
        if (!editMessageContainer) {
            editMessageContainer = Marionette.TemplateCache.get('#postTagsEditMessage')({});
            $(editMessageContainer).appendTo('body');
        }

        postTagManager = new views.PostTagManager({
            postsTable: postsTable,
            el: container
        });
        postTagManager.render();

        App.postTags.utils.updateBodyClass(true);
        postsTable.setProps('isInPostTagEditMode', true, {
            silent: true
        });

        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ViewedPostTagManager, {});
    }

    views.PostTagManager.showForPostsView = function (postsTable) {

        if (isPostTagManagerShowing) {
            postTagManager.setPostsTable(postsTable);
            return;
        }

        showPostTagManager(postsTable);
        isPostTagManagerShowing = true;
    };
}, window.moment);
